import React, { useState } from 'react';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowLeft, Send, CheckCircle, User, Mail, Phone, Building, Globe, MessageSquare } from 'lucide-react';
import { insertPackageSelection } from '../lib/supabase';
import { trackMetaPixelLead, trackMetaPixelContact } from '@/lib/meta-pixel';
import { CONTACT } from '@/config/contact';
import { useLanguage } from '@/contexts/LanguageContext';
import Seo from '@/components/Seo';
import { BASE_URL, getHreflangLinks, getPageMeta } from '@/seo/meta';
import { stripLanguagePrefix, withLanguagePrefix } from '@/lib/locale';

interface FormData {
  name: string;
  email: string;
  phone: string;
  company: string;
  website: string;
  address: string;
  city: string;
  selectedPackage: string;
  message: string;
  budget: string;
  timeline: string;
}

type Locale = 'en' | 'el';

type PackageCopy = {
  name: string;
  type: 'starter' | 'professional' | 'enterprise';
  price: number;
  description: string;
};

type Copy = {
  packages: PackageCopy[];
  budgetOptions: string[];
  timelineOptions: string[];
  submitError: string;
  successTitle: string;
  successMessage: (pkg: string) => string;
  successFollowUp: string;
  backHome: string;
  directContact: string;
  headerBack: string;
  headerEyebrow: string;
  headerTitle: string;
  sectionEyebrow: string;
  sectionTitle: string;
  sectionDescription: string;
  packageLabel: string;
  priceSuffix: string;
  labels: {
    name: string;
    company: string;
    email: string;
    phone: string;
    address: string;
    city: string;
    website: string;
    budget: string;
    timeline: string;
    message: string;
  };
  placeholders: {
    name: string;
    company: string;
    email: string;
    phone: string;
    address: string;
    city: string;
    website: string;
    budget: string;
    timeline: string;
    message: string;
  };
  submitLoading: string;
  submitLabel: string;
  requiredNote: string;
};

const copy: Record<Locale, Copy> = {
  en: {
    packages: [
      {
        name: 'Starter',
        type: 'starter',
        price: 699,
        description: 'For businesses starting their online presence',
      },
      {
        name: 'Growth',
        type: 'professional',
        price: 1299,
        description: 'For growing businesses that need a steady digital presence',
      },
      {
        name: 'Dominance',
        type: 'enterprise',
        price: 2299,
        description: 'For businesses that want to dominate their market',
      },
    ],
    budgetOptions: [
      'Under €1,000',
      '€1,000 - €3,000',
      '€3,000 - €5,000',
      '€5,000 - €10,000',
      'Over €10,000',
      "Let's discuss",
    ],
    timelineOptions: [
      'ASAP (1-2 weeks)',
      'Soon (1 month)',
      'Mid-term (2-3 months)',
      'Long-term (3+ months)',
      'Flexible timeline',
    ],
    submitError: 'There was an error submitting your request. Please try again.',
    successTitle: 'Thank you!',
    successMessage: (pkg: string) => `Your request for the ${pkg} package has been submitted successfully.`,
    successFollowUp: 'We will contact you within 24 hours to discuss the details.',
    backHome: 'Back to home',
    directContact: 'Or contact us directly:',
    headerBack: 'Back',
    headerEyebrow: 'Package selection',
    headerTitle: 'Select a package',
    sectionEyebrow: 'Business details',
    sectionTitle: 'Tell us about your business',
    sectionDescription:
      'Share your details so we can move forward with the right package.',
    packageLabel: 'Choose a package *',
    priceSuffix: '/month',
    labels: {
      name: 'Contact name *',
      company: 'Company name *',
      email: 'Email *',
      phone: 'Phone *',
      address: 'Address',
      city: 'City',
      website: 'Existing website',
      budget: 'Additional budget',
      timeline: 'Timeline',
      message: 'Additional information',
    },
    placeholders: {
      name: 'Your name',
      company: 'Company name',
      email: 'email@example.com',
      phone: '+1 555 123 4567',
      address: 'Company address',
      city: 'City',
      website: 'https://www.example.com',
      budget: 'Select budget',
      timeline: 'Select timeline',
      message: 'Tell us more about your needs, goals, or anything else you want us to know...',
    },
    submitLoading: 'Sending...',
    submitLabel: 'Send request',
    requiredNote: '* Required fields',
  },
  el: {
    packages: [
      {
        name: 'Starter',
        type: 'starter',
        price: 699,
        description: 'Για επιχειρήσεις που ξεκινούν την online προβολή τους',
      },
      {
        name: 'Growth',
        type: 'professional',
        price: 1299,
        description: 'Για επιχειρήσεις που αναπτύσσονται και χρειάζονται σταθερή ψηφιακή παρουσία',
      },
      {
        name: 'Dominance',
        type: 'enterprise',
        price: 2299,
        description: 'Για επιχειρήσεις που θέλουν να κυριαρχήσουν στον κλάδο τους',
      },
    ],
    budgetOptions: [
      'Κάτω από €1.000',
      '€1.000 - €3.000',
      '€3.000 - €5.000',
      '€5.000 - €10.000',
      'Πάνω από €10.000',
      'Θα συζητήσουμε',
    ],
    timelineOptions: [
      'Άμεσα (1-2 εβδομάδες)',
      'Σύντομα (1 μήνας)',
      'Μεσοπρόθεσμα (2-3 μήνες)',
      'Μακροπρόθεσμα (3+ μήνες)',
      'Ευέλικτο χρονοδιάγραμμα',
    ],
    submitError: 'Υπήρξε ένα σφάλμα κατά την αποστολή της αίτησης. Παρακαλώ δοκιμάστε ξανά.',
    successTitle: 'Ευχαριστούμε!',
    successMessage: (pkg: string) => `Η αίτησή σας για το ${pkg} πακέτο έχει σταλεί επιτυχώς.`,
    successFollowUp: 'Θα επικοινωνήσουμε μαζί σας εντός 24 ωρών για να συζητήσουμε τις λεπτομέρειες.',
    backHome: 'Επιστροφή στην Αρχική',
    directContact: 'Ή επικοινωνήστε απευθείας:',
    headerBack: 'Πίσω',
    headerEyebrow: 'Επιλογή πακέτου',
    headerTitle: 'Επιλογή Πακέτου',
    sectionEyebrow: 'Στοιχεία Επιχείρησης',
    sectionTitle: 'Συμπληρώστε τα στοιχεία σας',
    sectionDescription: 'Συμπληρώστε τα στοιχεία σας για να προχωρήσουμε με την επιλογή του κατάλληλου πακέτου.',
    packageLabel: 'Επιλέξτε Πακέτο *',
    priceSuffix: '/μήνα',
    labels: {
      name: 'Όνομα Επικοινωνίας *',
      company: 'Όνομα Επιχείρησης *',
      email: 'Email *',
      phone: 'Τηλέφωνο *',
      address: 'Διεύθυνση',
      city: 'Πόλη',
      website: 'Υπάρχων Ιστότοπος',
      budget: 'Επιπλέον Προϋπολογισμός',
      timeline: 'Χρονοδιάγραμμα',
      message: 'Επιπλέον Πληροφορίες',
    },
    placeholders: {
      name: 'Το όνομά σας',
      company: 'Όνομα επιχείρησης',
      email: 'email@example.com',
      phone: '+30 210 1234567',
      address: 'Διεύθυνση επιχείρησης',
      city: 'Πόλη',
      website: 'https://www.example.com',
      budget: 'Επιλέξτε προϋπολογισμό',
      timeline: 'Επιλέξτε χρονοδιάγραμμα',
      message: 'Πείτε μας περισσότερα για τις ανάγκες σας, τους στόχους σας, ή οτιδήποτε άλλο θα θέλατε να μας πείτε...',
    },
    submitLoading: 'Αποστολή...',
    submitLabel: 'Στείλτε την Αίτηση',
    requiredNote: '* Υποχρεωτικά πεδία',
  },
};

const PackageSelectionPage: React.FC = () => {
  const { language } = useLanguage();
  const location = useLocation();
  const locale = language === 'en' ? 'en' : 'el';
  const content = copy[locale];
  const meta = getPageMeta('package-selection', undefined, locale);
  const canonicalPath = withLanguagePrefix(stripLanguagePrefix(location.pathname), locale);
  const canonicalUrl = `${BASE_URL}${canonicalPath}`;
  const hreflang = getHreflangLinks(location.pathname, locale);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    company: '',
    website: '',
    address: '',
    city: '',
    selectedPackage: '',
    message: '',
    budget: '',
    timeline: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const packages = content.packages;
  const budgetOptions = content.budgetOptions;
  const timelineOptions = content.timelineOptions;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const selectedPkg = packages.find(pkg => pkg.name === formData.selectedPackage);
      const packageData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        website: formData.website || undefined,
        address: formData.address || undefined,
        city: formData.city || undefined,
        packageType: selectedPkg?.type as 'starter' | 'professional' | 'enterprise' | 'custom',
        status: 'pending' as const,
        message: formData.message || undefined,
        budget: formData.budget || undefined,
        timeline: formData.timeline || undefined
      };

      await insertPackageSelection(packageData);
      trackMetaPixelLead({
        content_name: 'Package Selection Form',
        package_name: formData.selectedPackage,
        package_type: selectedPkg?.type,
        page_path: window.location.pathname
      });
      trackMetaPixelContact({
        channel: 'form',
        form_name: 'package_selection',
        package_name: formData.selectedPackage,
        package_type: selectedPkg?.type,
        page_path: window.location.pathname
      });
      setIsSubmitted(true);
    } catch (error) {
      console.error('Error submitting package selection:', error);
      alert(content.submitError);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-black text-white p-4">
        <div className="container mx-auto max-w-2xl">
          <div className="text-center py-12 md:py-20">
            <div className="rounded-[32px] border border-white/15 bg-black/70 p-6 md:p-12 shadow-glass">
              <div className="mb-8">
                <CheckCircle className="h-16 w-16 md:h-20 md:w-20 text-brand-highlight mx-auto mb-6" />
                <h1 className="text-2xl md:text-4xl font-bold text-white mb-4">{content.successTitle}</h1>
                <p className="text-white/70 text-lg md:text-xl mb-6">
                  {content.successMessage(formData.selectedPackage)}
                </p>
                <p className="text-white/50 mb-8 text-sm md:text-base">
                  {content.successFollowUp}
                </p>
              </div>
              
              <div className="space-y-4">
                <button
                  onClick={() => window.navigateToHome?.()}
                  className="w-full rounded-full border border-white/10 bg-white py-4 px-6 text-sm font-semibold text-black transition-all duration-300 hover:border-brand-highlight/60 hover:bg-brand-highlight hover:text-white md:text-base"
                >
                  {content.backHome}
                </button>
                
                <div className="text-center text-white/50 text-sm">
                  <p>{content.directContact}</p>
                  <p className="mt-2">
                    <a href={`mailto:${CONTACT.email}`} className="text-white hover:text-brand-highlight">
                      {CONTACT.email}
                    </a>
                    {' | '}
                    <a href={CONTACT.phoneHref} className="text-white hover:text-brand-highlight">
                      {CONTACT.phoneDisplay}
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isSubmitDisabled =
    isSubmitting ||
    !formData.name ||
    !formData.email ||
    !formData.company ||
    !formData.phone ||
    !formData.selectedPackage;

  return (
    <div className="min-h-screen bg-black text-white p-4">
      <Seo
        title={meta.title}
        description={meta.description}
        url={canonicalUrl}
        hreflang={hreflang}
        image={meta.image}
      />
      <div className="container mx-auto max-w-4xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-10 rounded-2xl border border-white/15 bg-black/70 p-4 md:p-6 shadow-glass">
          <button
            onClick={() => window.navigateToHome?.()}
            className="flex items-center rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm text-white transition-colors duration-200 hover:border-brand-highlight/60 hover:text-brand-highlight md:px-4 md:text-base"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            {content.headerBack}
          </button>
          <div className="text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-highlight/80">
              {content.headerEyebrow}
            </p>
            <h1 className="mt-2 text-3xl font-semibold text-white md:text-4xl">
              {content.headerTitle}
            </h1>
          </div>
          <div className="w-16 md:w-20"></div>
        </div>

        {/* Form */}
        <div className="rounded-[32px] border border-white/15 bg-black/70 p-4 md:p-8 shadow-glass">
          <div className="mb-6 md:mb-8 text-center space-y-2">
            <p className="text-xs uppercase tracking-[0.35em] text-brand-highlight/80">{content.sectionEyebrow}</p>
            <h2 className="text-2xl font-semibold text-white md:text-3xl">{content.sectionTitle}</h2>
            <p className="text-white/60 text-sm md:text-base px-2">
              {content.sectionDescription}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6 md:space-y-8">
            {/* Package Selection */}
            <div>
              <label className="block text-white font-medium mb-4 text-sm md:text-base">
                {content.packageLabel}
              </label>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                {packages.map((pkg) => (
                  <label key={pkg.name} className="cursor-pointer">
                    <input
                      type="radio"
                      name="selectedPackage"
                      value={pkg.name}
                      checked={formData.selectedPackage === pkg.name}
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                    <div 
                      className={`rounded-2xl border p-5 transition-all duration-200 ${
                        formData.selectedPackage === pkg.name
                          ? 'border-brand-highlight/80 bg-brand-highlight/10 text-white shadow-glass'
                          : 'border-white/15 bg-black/80 hover:border-white/30'
                      }`}
                    >
                      <h3 className="text-lg font-semibold text-white">{pkg.name}</h3>
                      <p className="my-2 text-xl font-semibold text-white/80">€{pkg.price}{content.priceSuffix}</p>
                      <p className="text-sm text-white/60">{pkg.description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Business Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <div>
                <label className="block text-white font-medium mb-2 text-sm md:text-base">
                  <User className="inline h-4 w-4 mr-2" />
                  {content.labels.name}
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full rounded-lg border border-white/15 bg-black/60 px-4 py-3 text-white placeholder-white/40 transition focus:outline-none focus:border-brand-highlight/70 focus:ring-2 focus:ring-brand-highlight/60 text-sm md:text-base"
                  placeholder={content.placeholders.name}
                />
              </div>

              <div>
                <label className="block text-white font-medium mb-2 text-sm md:text-base">
                  <Building className="inline h-4 w-4 mr-2" />
                  {content.labels.company}
                </label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  required
                  className="w-full rounded-lg border border-white/15 bg-black/60 px-4 py-3 text-white placeholder-white/40 transition focus:outline-none focus:border-brand-highlight/70 focus:ring-2 focus:ring-brand-highlight/60 text-sm md:text-base"
                  placeholder={content.placeholders.company}
                />
              </div>

              <div>
                <label className="block text-white font-medium mb-2 text-sm md:text-base">
                  <Mail className="inline h-4 w-4 mr-2" />
                  {content.labels.email}
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full rounded-lg border border-white/15 bg-black/60 px-4 py-3 text-white placeholder-white/40 transition focus:outline-none focus:border-brand-highlight/70 focus:ring-2 focus:ring-brand-highlight/60 text-sm md:text-base"
                  placeholder={content.placeholders.email}
                />
              </div>

              <div>
                <label className="block text-white font-medium mb-2 text-sm md:text-base">
                  <Phone className="inline h-4 w-4 mr-2" />
                  {content.labels.phone}
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="w-full rounded-lg border border-white/15 bg-black/60 px-4 py-3 text-white placeholder-white/40 transition focus:outline-none focus:border-brand-highlight/70 focus:ring-2 focus:ring-brand-highlight/60 text-sm md:text-base"
                  placeholder={content.placeholders.phone}
                />
              </div>
            </div>

            {/* Address Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <div>
                <label className="block text-white font-medium mb-2 text-sm md:text-base">
                  {content.labels.address}
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border border-white/15 bg-black/60 px-4 py-3 text-white placeholder-white/40 transition focus:outline-none focus:border-brand-highlight/70 focus:ring-2 focus:ring-brand-highlight/60 text-sm md:text-base"
                  placeholder={content.placeholders.address}
                />
              </div>

              <div>
                <label className="block text-white font-medium mb-2 text-sm md:text-base">
                  {content.labels.city}
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border border-white/15 bg-black/60 px-4 py-3 text-white placeholder-white/40 transition focus:outline-none focus:border-brand-highlight/70 focus:ring-2 focus:ring-brand-highlight/60 text-sm md:text-base"
                  placeholder={content.placeholders.city}
                />
              </div>
            </div>

            {/* Website */}
            <div>
              <label className="block text-white font-medium mb-2 text-sm md:text-base">
                <Globe className="inline h-4 w-4 mr-2" />
                {content.labels.website}
              </label>
              <input
                type="url"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                className="w-full rounded-lg border border-white/15 bg-black/60 px-4 py-3 text-white placeholder-white/40 transition focus:outline-none focus:border-brand-highlight/70 focus:ring-2 focus:ring-brand-highlight/60 text-sm md:text-base"
                placeholder={content.placeholders.website}
              />
            </div>

            {/* Budget and Timeline */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <div>
                <label className="block text-white font-medium mb-2 text-sm md:text-base">
                  {content.labels.budget}
                </label>
                <select
                  name="budget"
                  value={formData.budget}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border border-white/15 bg-black/60 px-4 py-3 text-white transition focus:outline-none focus:border-brand-highlight/70 focus:ring-2 focus:ring-brand-highlight/60 text-sm md:text-base"
                >
                  <option value="">{content.placeholders.budget}</option>
                  {budgetOptions.map((budget) => (
                    <option key={budget} value={budget} className="bg-black text-white">
                      {budget}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-white font-medium mb-2 text-sm md:text-base">
                  {content.labels.timeline}
                </label>
                <select
                  name="timeline"
                  value={formData.timeline}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border border-white/15 bg-black/60 px-4 py-3 text-white transition focus:outline-none focus:border-brand-highlight/70 focus:ring-2 focus:ring-brand-highlight/60 text-sm md:text-base"
                >
                  <option value="">{content.placeholders.timeline}</option>
                  {timelineOptions.map((timeline) => (
                    <option key={timeline} value={timeline} className="bg-black text-white">
                      {timeline}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Message */}
            <div>
              <label className="block text-white font-medium mb-2 text-sm md:text-base">
                <MessageSquare className="inline h-4 w-4 mr-2" />
                {content.labels.message}
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                rows={4}
                className="w-full resize-none rounded-lg border border-white/15 bg-black/60 px-4 py-3 text-white placeholder-white/40 transition focus:outline-none focus:border-brand-highlight/70 focus:ring-2 focus:ring-brand-highlight/60 text-sm md:text-base"
                placeholder={content.placeholders.message}
              />
            </div>

            {/* Submit Button */}
            <div className="text-center">
              <button
                type="submit"
                disabled={isSubmitDisabled}
                className={`mx-auto flex min-w-[200px] items-center justify-center rounded-full border px-6 py-4 text-sm font-semibold transition-all duration-300 md:px-8 md:text-base ${
                  isSubmitDisabled
                    ? 'cursor-not-allowed border-white/10 bg-white/5 text-white/40'
                    : 'border-white/10 bg-white text-black hover:border-brand-highlight/70 hover:bg-brand-highlight hover:text-white shadow-glass'
                }`}
              >
                {isSubmitting ? (
                  <>
                    <div className="mr-2 h-5 w-5 animate-spin rounded-full border-b-2 border-black/40"></div>
                    {content.submitLoading}
                  </>
                ) : (
                  <>
                    {content.submitLabel}
                    <Send className="ml-2 h-5 w-5" />
                  </>
                )}
              </button>
              
              <p className="mt-4 text-sm text-white/50">
                {content.requiredNote}
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PackageSelectionPage;
